import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IAdoptionApplication from '../../interfaces/IAdoptionApplication'
import {
  SearchRequest,
  AdoptionApplicationReturnRequest,
  AdoptionApplicationCheckoutRemoveProductRequest,
  AdoptionApplicationCheckoutUpdateRequest,
  AdoptionApplicationForceSetAdoptedRequest
} from '../../services/api/interfaces/ApiRequest'
import {
  adoptionApplicationList,
  adoptionApplicationReturn,
  adoptionApplicationCheckoutRemoveProduct,
  adoptionApplicationCheckoutUpdate,
  adoptionApplicationForceSetAdopted,
  adoptionApplicationForceSetAvailable
} from '../../services/api'

interface AdoptionApplicationContextState extends BaseContextState {
  applications: Array<IAdoptionApplication> | null,
  application: IAdoptionApplication | null,
}

interface AdoptionApplicationContextData extends AdoptionApplicationContextState {
  resetErrors: Function,
  requestAdoptionApplicationList: (args: SearchRequest) => void,
  requestAdoptionApplicationFind: (args: SearchRequest) => void,
  requestAdoptionApplicationReturn: (args: AdoptionApplicationReturnRequest) => Promise<any>,
  requestAdoptionApplicationCheckoutRemoveProduct: (args: AdoptionApplicationCheckoutRemoveProductRequest) => Promise<any>,
  requestAdoptionApplicationCheckoutUpdate: (args: AdoptionApplicationCheckoutUpdateRequest) => Promise<any>,
  requestAdoptionApplicationForceSetAdopted: (args: AdoptionApplicationForceSetAdoptedRequest) => Promise<any>,
  requestAdoptionApplicationForceSetAvailable: (args: any) => Promise<any>,
}

// Create context
const { Provider, Consumer } = createContext({} as AdoptionApplicationContextData)

// Export consumer
export const AdoptionApplicationConsumer = Consumer

export class AdoptionApplicationProvider extends BaseContextProvider {
  state: AdoptionApplicationContextState = {
    applications: null,
    application: null
  }

  lastListPayload: SearchRequest | null = null

  /**
   * Make api request for adoption applications list
   * 
   * @param {SearchRequest} payload 
   */
  requestAdoptionApplicationList = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null
    }, async () => {
      this.lastListPayload = payload
      const response = await adoptionApplicationList(payload)

      // Process our response
      this.processResponse(response, { 
        paginate: {
          destination: 'applications'
        },
      })
    })
  }

  /**
   * Make api request for adoption applications finder
   * 
   * @param {SearchRequest} payload 
   */
  requestAdoptionApplicationFind = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      application: null
    }, async () => {
      payload.options = {
        ...payload.options,
        _single: true
      }

      const response = await adoptionApplicationList(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: {
          destination: 'application'
        },
      })
    })
  }

  /**
   * Make api request for adoption applications update checkout
   * 
   * @param {AdoptionApplicationCheckoutUpdateRequest} payload 
   */
  requestAdoptionApplicationCheckoutUpdate = (payload: AdoptionApplicationCheckoutUpdateRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await adoptionApplicationCheckoutUpdate(payload)
        this.processResponse(response)

        if (this.state.success) {
          this.requestAdoptionApplicationFind({
            search: {
              uuid: payload.application_uuid,
              checkout: {
                with: {
                  deleted: false
                }
              },
              adopter_user: {
                with: true
              },
              "pet": {
                "with": {
                    "specie": {
                        "with": true
                    },
                    "branch": {
                        "with": {
                            "shelter": {
                                "with": true
                            }
                        }
                    }
                }
              }
            }
          })

          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for adoption applications return
   * 
   * @param {AdoptionApplicationReturnRequest} payload 
   */
  requestAdoptionApplicationReturn = (payload: AdoptionApplicationReturnRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await adoptionApplicationReturn(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          this.requestAdoptionApplicationList(this.lastListPayload || {})
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for adoption applications checkout remove product
   * 
   * @param {AdoptionApplicationCheckoutRemoveProductRequest} payload 
   */
  requestAdoptionApplicationCheckoutRemoveProduct = (payload: AdoptionApplicationCheckoutRemoveProductRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await adoptionApplicationCheckoutRemoveProduct(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          this.requestAdoptionApplicationFind({
            search: {
              uuid: payload.application_uuid,
              checkout: {
                with: true
              },
              adopter_user: {
                with: true
              },
              "pet": {
                "with": {
                    "specie": {
                        "with": true
                    },
                    "branch": {
                        "with": {
                            "shelter": {
                                "with": true
                            }
                        }
                    }
                }
              }
            }
          })
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for adoption applications force set adopted
   * 
   * @param {AdoptionApplicationForceSetAdoptedRequest} payload 
   */
  requestAdoptionApplicationForceSetAvailable = (payload: any) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await adoptionApplicationForceSetAvailable(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          resolve(response)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for adoption applications force set adopted
   * 
   * @param {AdoptionApplicationForceSetAdoptedRequest} payload 
   */
  requestAdoptionApplicationForceSetAdopted = (payload: AdoptionApplicationForceSetAdoptedRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await adoptionApplicationForceSetAdopted(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          this.requestAdoptionApplicationList(this.lastListPayload || {})
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestAdoptionApplicationList: this.requestAdoptionApplicationList,
          requestAdoptionApplicationFind: this.requestAdoptionApplicationFind,
          requestAdoptionApplicationCheckoutUpdate: this.requestAdoptionApplicationCheckoutUpdate,
          requestAdoptionApplicationReturn: this.requestAdoptionApplicationReturn,
          requestAdoptionApplicationCheckoutRemoveProduct: this.requestAdoptionApplicationCheckoutRemoveProduct,
          requestAdoptionApplicationForceSetAdopted: this.requestAdoptionApplicationForceSetAdopted,
          requestAdoptionApplicationForceSetAvailable: this.requestAdoptionApplicationForceSetAvailable
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
